import { GridColDef } from '@mui/x-data-grid-premium';
import { CustomDataGrid, StackedTextCell } from '@retail/components';
import { useRowColorGrouping } from '@retail/hooks';
import { useMyAssortmentTFunction } from '@retail/my-assortment/i18n';
import { PriceControlValidate } from '@retail/price-control/types';
import { useMgItemAndExternalNumberColumn } from '@retail/products/components';
import { formatPrice } from '@retail/products/utils';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  items: PriceControlValidate[];
  externalItemSource: 'NOBB' | 'FINFO';
  isLoading: boolean;
  includeVat: boolean;
}

const baseColumnProps: Partial<GridColDef> = {
  width: 140,
  headerName: '',
  headerAlign: 'left',
  align: 'left',
  flex: 1,
};
export function HandleItemsEventActionTable({
  items,
  externalItemSource,
  isLoading,
  includeVat,
}: Props) {
  const t = useMyAssortmentTFunction();
  const { getRowColorForStripedTable } = useRowColorGrouping();
  const {
    i18n: { language },
  } = useTranslation();

  const mgItemAndExternalNumberColumn = useMgItemAndExternalNumberColumn<PriceControlValidate>({
    externalItemSource,
    columnProps: baseColumnProps,
  });

  const columns: GridColDef<PriceControlValidate>[] = useMemo(
    () => [
      mgItemAndExternalNumberColumn,
      {
        ...baseColumnProps,
        field: 'itemText',
        width: 260,
        headerName: t('myAssortment.columns.itemText'),
        renderCell: ({ row }) => (
          <StackedTextCell topLine={row.primaryText} bottomLine={row.secondaryText} />
        ),
        valueGetter: (_, row) => row.primaryText,
      },

      {
        ...baseColumnProps,
        field: 'salesPrice',
        renderHeader: () => (
          <StackedTextCell
            topLine={t('myAssortment.columns.salesPrice')}
            bottomLine={
              includeVat ? t('myAssortment.columns.incVat') : t('myAssortment.columns.exVat')
            }
          />
        ),
        width: 210,
        renderCell: ({ row }) => {
          const newSalesPrice = includeVat
            ? row.result.newSalesPriceIncVat
            : row.result.newSalesPriceExVat;
          const previousSalesPrice = includeVat
            ? row.previousSalesPriceIncVat
            : row.previousSalesPriceExVat;

          return (
            <StackedTextCell
              nullableBottomLine
              topLine={newSalesPrice ? `${formatPrice(newSalesPrice, language)} kr` : '-'}
              bottomLine={
                previousSalesPrice ? `${formatPrice(previousSalesPrice, language)} kr` : undefined
              }
              slotProps={{ bottomLine: { lineThrough: true } }}
            />
          );
        },
      },
      {
        ...baseColumnProps,
        field: 'salesCoverage',
        headerName: t('myAssortment.salesCoverage'),
        width: 210,
        renderCell: ({ row }) => (
          <StackedTextCell
            nullableBottomLine
            topLine={row.result.newCoverageDegree ? `${row.result.newCoverageDegree}%` : '-'}
            bottomLine={row.previousCoverageDegree ? `${row.previousCoverageDegree}%` : undefined}
            slotProps={{ bottomLine: { lineThrough: true } }}
          />
        ),
      },
      {
        ...baseColumnProps,
        field: 'salesFactorChange',
        headerName: t('myAssortment.columns.freightCondition.factor'),
        width: 210,
        renderCell: ({ row }) => {
          return (
            <StackedTextCell
              nullableBottomLine
              topLine={row.result.newSalesFactor ? row.result.newSalesFactor : '-'}
              bottomLine={row.previousSalesFactor}
              slotProps={{ bottomLine: { lineThrough: true } }}
            />
          );
        },
      },
    ],
    [includeVat, language, mgItemAndExternalNumberColumn, t]
  );

  return (
    <CustomDataGrid<PriceControlValidate>
      disableRowSelectionOnClick
      getRowClassName={(params) =>
        params.row.isValid ? getRowColorForStripedTable(params) : 'invalid-row'
      }
      getRowId={(row) => row.memberEventId}
      rowHeight={70}
      disableColumnMenu
      hideFooter
      rows={items}
      columns={columns}
      loading={isLoading}
      sx={{
        '& .invalid-row': {
          backgroundColor: 'grey.100',
        },
      }}
    />
  );
}
